import React, { useState, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  IconButton,
  Text,
  Select,
  useColorModeValue,
  Box,
} from '@chakra-ui/react';
import { MdAdd, MdDelete } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { getTimeZones, TimeZone } from '@vvo/tzdb'; // Correct function name and import type

interface TimezoneSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTimezones: string[];
  onTimezonesChange: (timezones: string[]) => void;
  mainTimezone: string; // Display the main timezone as well
}

export const TimezoneSettingsModal: React.FC<TimezoneSettingsModalProps> = ({
  isOpen,
  onClose,
  selectedTimezones,
  onTimezonesChange,
  mainTimezone,
}) => {
  const { t } = useTranslation(['v0/calendar', 'v0/common']);
  const [newTimezone, setNewTimezone] = useState('');
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Get the full list of timezone names from the library
  // listTimeZones() returns objects, we only need the name property
  const availableTimezones = useMemo(() => {
    return getTimeZones().map((tz: TimeZone) => tz.name).sort();
  }, []);

  // Filter out already selected timezones and the main timezone
  const selectableTimezones = useMemo(() => {
    const currentSelection = new Set([mainTimezone, ...selectedTimezones]);
    return availableTimezones.filter((tz: string) => !currentSelection.has(tz));
  }, [availableTimezones, selectedTimezones, mainTimezone]);

  const handleAddTimezone = () => {
    if (newTimezone && !selectedTimezones.includes(newTimezone)) {
      onTimezonesChange([...selectedTimezones, newTimezone]);
      setNewTimezone(''); // Reset select
    }
  };

  const handleRemoveTimezone = (timezoneToRemove: string) => {
    onTimezonesChange(selectedTimezones.filter((tz: string) => tz !== timezoneToRemove));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <ModalHeader>{t('v0/calendar:timezoneSettings.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Box>
              <Text fontWeight="bold" mb={2}>{t('v0/calendar:timezoneSettings.mainTimezone')}:</Text>
              <Text>{mainTimezone} ({t('v0/calendar:timezoneSettings.mainTimezoneDesc')})</Text>
            </Box>

            <Box borderTopWidth="1px" borderColor={borderColor} pt={4}>
              <Text fontWeight="bold" mb={2}>{t('v0/calendar:timezoneSettings.additionalTimezones')}:</Text>
              {selectedTimezones.length === 0 ? (
                 <Text fontStyle="italic" color="gray.500">{t('v0/calendar:timezoneSettings.noAdditional')}</Text>
              ) : (
                 <VStack spacing={2} align="stretch">
                   {selectedTimezones.map((tz: string) => (
                     <HStack key={tz} justify="space-between" p={2} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                       <Text>{tz}</Text>
                       <IconButton
                         size="xs"
                         colorScheme="red"
                         aria-label={t('v0/common:actions.delete')}
                         icon={<MdDelete />}
                         onClick={() => handleRemoveTimezone(tz)}
                       />
                     </HStack>
                   ))}
                 </VStack>
              )}
            </Box>

            <HStack pt={4} borderTopWidth="1px" borderColor={borderColor}>
              <Select
                placeholder={t('v0/calendar:timezoneSettings.selectToAdd')}
                value={newTimezone}
                onChange={(e) => setNewTimezone(e.target.value)}
              >
                {selectableTimezones.map((tz: string) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </Select>
              <IconButton
                colorScheme="blue"
                aria-label={t('v0/calendar:timezoneSettings.add')}
                icon={<MdAdd />}
                onClick={handleAddTimezone}
                isDisabled={!newTimezone}
              />
            </HStack>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>{t('v0/common:actions.close')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};